import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Users, 
  Zap, 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Clock
} from 'lucide-react';
import { autoscalingService } from '../services/autoscaling';

interface AutoscalingMonitorProps {
  className?: string;
  showHeader?: boolean;
  compact?: boolean;
}

interface MonitoringData {
  enabled: boolean;
  status: 'healthy' | 'scaling' | 'error' | 'disabled';
  workers: {
    active: number;
    target: number;
    capacity: number;
  };
  concurrency: {
    current: number;
    max: number;
    utilization: number;
  };
  queue: {
    depth: number;
    processingRate: number;
    avgTime: number;
  };
  lastAction?: {
    action: string;
    timestamp: string;
    reason: string;
  };
}

export function AutoscalingMonitor({ 
  className = '', 
  showHeader = true, 
  compact = false 
}: AutoscalingMonitorProps) {
  const [data, setData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const loadMonitoringData = async () => {
    try {
      const monitoringData = await autoscalingService.getMonitoringData();
      setData(monitoringData);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load monitoring data');
      console.error('Error loading autoscaling monitoring data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Real-time updates every 10 seconds
  useEffect(() => {
    loadMonitoringData();
    
    const interval = setInterval(loadMonitoringData, 10000); // 10 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'scaling':
        return <Activity className="h-5 w-5 text-blue-500 animate-pulse" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'disabled':
        return <Minus className="h-5 w-5 text-gray-400" />;
      default:
        return <Activity className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'scaling':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'disabled':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'scale_up':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'scale_down':
        return <TrendingDown className="h-4 w-4 text-blue-600" />;
      case 'maintain':
        return <Minus className="h-4 w-4 text-gray-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg border border-red-200 ${className}`}>
        <div className="p-4">
          <div className="flex items-center text-red-600">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span className="text-sm">Failed to load autoscaling data</span>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 relative overflow-hidden ${className}`}>
      {/* Header */}
      {showHeader && (
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Zap className="h-5 w-5 text-blue-600 mr-2" />
              <h3 className="text-sm font-medium text-gray-900">Queue Autoscaling</h3>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Status Badge */}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(data.status)}`}>
                {getStatusIcon(data.status)}
                <span className="ml-1 capitalize">{data.status}</span>
              </span>
              
              {/* Refresh Button */}
              <button
                onClick={loadMonitoringData}
                disabled={loading}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {!data.enabled ? (
          <div className="text-center py-4 text-gray-500">
            <Minus className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">Autoscaling is disabled</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Metrics Grid */}
            <div className={`grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
              {/* Workers */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-xs font-medium text-blue-900">Workers</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-700">{data.workers.active}</div>
                    <div className="text-xs text-blue-600">
                      {data.workers.target !== data.workers.active && (
                        <span>→ {data.workers.target} / </span>
                      )}
                      max {data.workers.max}
                    </div>
                  </div>
                </div>
              </div>

              {/* Concurrency */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 text-purple-600 mr-2" />
                    <span className="text-xs font-medium text-purple-900">Concurrency</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-700">{data.concurrency.current}</div>
                    <div className="text-xs text-purple-600">
                      max {data.concurrency.max}
                    </div>
                  </div>
                </div>
              </div>

              {/* Queue Depth */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <BarChart3 className="h-4 w-4 text-orange-600 mr-2" />
                    <span className="text-xs font-medium text-orange-900">Queue</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-orange-700">{data.queue.depth}</div>
                    <div className="text-xs text-orange-600">posts</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Action */}
            {data.lastAction && !compact && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getActionIcon(data.lastAction.action)}
                    <div className="ml-2">
                      <div className="text-xs font-medium text-gray-900 capitalize">
                        {data.lastAction.action.replace('_', ' ')}
                      </div>
                      <div className="text-xs text-gray-600 truncate max-w-40">
                        {data.lastAction.reason}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {new Date(data.lastAction.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Last Update */}
            {!compact && (
              <div className="text-xs text-gray-400 text-center">
                Updated: {lastUpdate.toLocaleTimeString()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}